import cloneDeep from "lodash/cloneDeep";
import { DISCOUNT_MODES } from "../data/constants/shared";
import type { BillDiscount, Sale } from "../promotions/types";

/**
 * Taking a discount off a bill.
 *
 * 🔴 **`cloneDeep`, NOT `structuredClone`.** Hermes has no `structuredClone`; apps/mobile
 * polyfills it at `index.js:4`, and shared code that depends on a host's bootstrap order breaks
 * silently — worse, node HAS it natively, so every test here would pass while the mobile app
 * crashed. The same substitution is recorded in `catalog/catalogModel.ts`, which is
 * where this rule is written down; `apps/web`'s own discount code uses `structuredClone` and
 * keeps it, because a browser has one.
 *
 * Pure and CLONE-ONLY: nothing here mutates its argument, and nothing here prices or saves.
 */

/**
 * A CLONE of `sale` with one discount removed from `billDiscountList`.
 *
 * ⚠️ **An auto discount is not deleted — its `priority` is set to `-1`** so the pricing engine
 * re-evaluates it as unapplied. Deleting it would let the engine put it straight back on the
 * next recalculation, which is why the suppression marker exists at all.
 *
 * This is the LOCAL half of a removal only. A partner coupon additionally needs releasing —
 * `dropDiscount` in `./release` pairs the two.
 */
export const stripBillDiscount = (sale: Sale, discount: BillDiscount): Sale => {
  const next: any = cloneDeep(sale);
  const list: any[] = next.billDiscountList;
  if (!list?.length) return next;

  const index = list.findIndex((entry: any) => entry?.discountId === (discount as any)?.discountId);
  if (index > -1) {
    if (list[index]?.mode === DISCOUNT_MODES.auto) list[index].priority = -1;
    else list.splice(index, 1);
  }
  next.billDiscountList = list;
  return next;
};
